// Answers set by hand in the preview, so an author can watch the document change as a
// recipient's data would change it.
//
// Applied to the DATA, not to the rules. Flipping a rule on directly would let the author build
// a recipient who cannot exist — "US investor" and "non-US investor" both shown — and would leave
// a field printing "United States" beside a clause for somebody who is not. Writing the answer
// into the recipient's data makes every rule reading it, and every field printing it, agree.
import { z } from 'zod';

import type { SmartFieldsAvailableSources } from '../smartFields';
import { humaniseFieldName, splitFilters } from '../documentTemplate';
import { choiceSourceForPath } from '../../../documents/dataPointChoices';
import { FIELD_BINDING_CATALOGUE } from '../../../documents/fieldBindingCatalogue';
import {
	answeredPathFor,
	isBlankFlowValue,
	listPointParts,
	readFlowDataPath,
	withFlowDataValue,
} from './flowDataPath';
import type {
	FlowPredicate,
	FlowPredicateEvaluator,
	FlowRule,
	FlowRuleDecision,
	FlowRules,
	FlowTernary,
} from './rules';
import { decideFlowRule } from './rules';

const scenarioAnswerSchema = z.discriminatedUnion('state', [
	z.object({ state: z.literal('yes') }),
	z.object({ state: z.literal('no') }),
	z.object({ state: z.literal('notAnswered') }),
	z.object({ state: z.literal('blank') }),
	z.object({ state: z.literal('filled') }),
	z.object({ state: z.literal('value'), value: z.string().min(1) }),
]);
export type FlowScenarioAnswer = z.infer<typeof scenarioAnswerSchema>;

export const flowRuleScenarioSchema = z.object({
	/** Keyed by data point path. A path no rule reads is ignored, never written. */
	dataPoints: z.record(scenarioAnswerSchema),
	/** Keyed by cohort id: whether the recipient is in it. */
	cohorts: z.record(z.boolean()),
});
export type FlowRuleScenario = z.infer<typeof flowRuleScenarioSchema>;

export const EMPTY_FLOW_RULE_SCENARIO: FlowRuleScenario = { dataPoints: {}, cohorts: {} };

/** The control a data point needs, from the tests the rules ask of it. */
export type FlowRuleInputKind = 'yesNo' | 'choice' | 'filled';

/** One thing the preview can set, and the rules that read it. */
export type FlowRuleInput =
	| { kind: FlowRuleInputKind; dataPointPath: string; ruleIds: string[] }
	| { kind: 'cohort'; cohortId: string; cohortName?: string; ruleIds: string[] };

const KIND_BY_OPERATOR: Record<
	Exclude<FlowPredicate['operator'], 'inCohort'>,
	FlowRuleInputKind
> = {
	isYes: 'yesNo',
	isNo: 'yesNo',
	isOneOf: 'choice',
	isNoneOf: 'choice',
	isBlank: 'filled',
	isNotBlank: 'filled',
};

// A point asked two kinds of question gets the richest control it can actually RENDER, which is
// why neither half of this is a plain table. A choice list carries "not recorded", so it covers
// "is filled in" too and outranks it — but only where the point declares where its values come
// from. A declaration does not, so a `choice` test on one would otherwise ask the panel for a
// picker it cannot build, leaving the author no control at all.
const KIND_RANK: Record<FlowRuleInputKind, number> = { filled: 0, yesNo: 1, choice: 2 };

const renderableKind = (dataPointPath: string, kind: FlowRuleInputKind): FlowRuleInputKind =>
	kind === 'choice' && choiceSourceForPath(dataPointPath) === undefined ? 'filled' : kind;

/** Every data point and cohort the rules read, once each, in the order the rules name them. */
export const flowRuleInputs = (rules: FlowRules): FlowRuleInput[] => {
	const points = new Map<string, { kind: FlowRuleInputKind; ruleIds: string[] }>();
	const cohorts = new Map<string, { cohortName?: string; ruleIds: string[] }>();
	for (const [ruleId, { when }] of Object.entries(rules)) {
		if (when.operator === 'inCohort') {
			const seen = cohorts.get(when.cohortId);
			cohorts.set(when.cohortId, {
				...(seen?.cohortName === undefined && when.cohortName === undefined
					? {}
					: { cohortName: seen?.cohortName ?? when.cohortName }),
				ruleIds: [...(seen?.ruleIds ?? []), ruleId],
			});
			continue;
		}
		const kind = renderableKind(when.dataPointPath, KIND_BY_OPERATOR[when.operator]);
		const seen = points.get(when.dataPointPath);
		points.set(when.dataPointPath, {
			kind: seen !== undefined && KIND_RANK[seen.kind] > KIND_RANK[kind] ? seen.kind : kind,
			ruleIds: [...(seen?.ruleIds ?? []), ruleId],
		});
	}
	return [
		...[...points].map(
			([dataPointPath, point]): FlowRuleInput => ({ dataPointPath, ...point })
		),
		...[...cohorts].map(
			([cohortId, cohort]): FlowRuleInput => ({ kind: 'cohort', cohortId, ...cohort })
		),
	];
};

/** What a field set to "filled in" prints when the recipient has nothing there. */
const placeholderFor = (path: string, expression: string): string => {
	const label =
		FIELD_BINDING_CATALOGUE.find((point) => point.path === path)?.label ??
		humaniseFieldName(expression.split('.').at(-1) ?? expression);
	return `«${label}»`;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * An answer for a point read off a list, written as the WHOLE list. Rules read every entry, so
 * setting only the first would leave another entry still matching.
 */
const withListAnswer = (
	data: SmartFieldsAvailableSources,
	path: string,
	{ list, field }: { list: string; field: string },
	answer: FlowScenarioAnswer
): SmartFieldsAvailableSources | null => {
	const current = readFlowDataPath({ data }, list);
	const entries = Array.isArray(current) ? current : [];
	const first = isRecord(entries[0]) ? entries[0] : {};
	switch (answer.state) {
		case 'value':
			return withFlowDataValue(data, list, [{ ...first, [field]: answer.value }]);
		case 'blank':
			return withFlowDataValue(data, list, []);
		case 'filled':
			return entries.some((entry) => !isBlankFlowValue(readFlowDataPath(entry, field)))
				? data
				: withFlowDataValue(data, list, [
						{ ...first, [field]: placeholderFor(path, `${list}[0].${field}`) },
					]);
		default:
			return null;
	}
};

const withAnswer = (
	data: SmartFieldsAvailableSources,
	path: string,
	answer: FlowScenarioAnswer
): SmartFieldsAvailableSources => {
	const expression = splitFilters(path).expression;
	const listPoint = listPointParts(expression);
	const asList = listPoint === null ? null : withListAnswer(data, path, listPoint, answer);
	if (asList !== null) {
		return asList;
	}
	const answered = answeredPathFor(expression);
	switch (answer.state) {
		case 'yes':
		case 'no': {
			const set = withFlowDataValue(data, expression, answer.state === 'yes');
			return answered === null ? set : withFlowDataValue(set, answered, true);
		}
		case 'notAnswered': {
			// What the projection holds for a declaration nobody has made: `false`, with its
			// `answered` twin saying so.
			const set = withFlowDataValue(data, expression, answered === null ? null : false);
			return answered === null ? set : withFlowDataValue(set, answered, false);
		}
		case 'blank':
			return withFlowDataValue(data, expression, null);
		case 'filled':
			// The recipient's own value where there is one, so the page still reads as theirs.
			return isBlankFlowValue(readFlowDataPath({ data }, expression))
				? withFlowDataValue(data, expression, placeholderFor(path, expression))
				: data;
		case 'value':
			return withFlowDataValue(data, expression, answer.value);
	}
};

const withCohort = (
	data: SmartFieldsAvailableSources,
	cohortId: string,
	cohortName: string | undefined,
	inCohort: boolean
): SmartFieldsAvailableSources => {
	const isMember = data.investor.cohorts.some((cohort) => cohort.id === cohortId);
	if (isMember === inCohort) {
		return data;
	}
	const cohorts = inCohort
		? [...data.investor.cohorts, { id: cohortId, name: cohortName ?? cohortId, tags: [] }]
		: data.investor.cohorts.filter((cohort) => cohort.id !== cohortId);
	return { ...data, investor: { ...data.investor, cohorts } };
};

const ownEntry = <T>(record: Record<string, T>, key: string): T | undefined =>
	Object.hasOwn(record, key) ? record[key] : undefined;

/**
 * The recipient's data with the scenario's answers written in.
 *
 * Only the data points and cohorts the rules actually read are touched, so a scenario cannot be
 * used to rewrite anything else a field prints.
 */
export const applyFlowRuleScenario = (
	data: SmartFieldsAvailableSources,
	scenario: FlowRuleScenario,
	rules: FlowRules
): SmartFieldsAvailableSources =>
	flowRuleInputs(rules).reduce((next, input) => {
		if (input.kind === 'cohort') {
			const inCohort = ownEntry(scenario.cohorts, input.cohortId);
			return inCohort === undefined
				? next
				: withCohort(next, input.cohortId, input.cohortName, inCohort);
		}
		const answer = ownEntry(scenario.dataPoints, input.dataPointPath);
		return answer === undefined ? next : withAnswer(next, input.dataPointPath, answer);
	}, data);

/** Whether the scenario sets what this rule reads — all a view with no recipient can decide on. */
export const scenarioSetsRuleInput =
	(scenario: FlowRuleScenario) =>
	(rule: FlowRule): boolean =>
		rule.when.operator === 'inCohort'
			? ownEntry(scenario.cohorts, rule.when.cohortId) !== undefined
			: ownEntry(scenario.dataPoints, rule.when.dataPointPath) !== undefined;

/** What the preview did with one rule. `undecided` content is kept and labelled. */
export type FlowRulePreviewDecision = FlowRuleDecision | 'undecided';

export interface FlowRuleOutcome {
	ruleId: string;
	decision: FlowRulePreviewDecision;
	/** Null when the rule was left undecided, so nothing was asked. */
	answer: FlowTernary | null;
}

/** Each rule's outcome for the preview's recipient, with the answer it was decided on. */
export const flowRuleOutcomes = (
	rules: FlowRules,
	evaluate: FlowPredicateEvaluator,
	canDecide: (rule: FlowRule) => boolean = () => true
): FlowRuleOutcome[] =>
	Object.entries(rules).map(([ruleId, rule]) =>
		canDecide(rule)
			? { ruleId, ...decideFlowRule(rule, evaluate) }
			: { ruleId, decision: 'undecided', answer: null }
	);
